import { useMemo } from "react";
import { Chip, Stack } from "@mui/material";

import { CellContext } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { DataTable } from "components/DataTable/DataTable";
import {
  getZoneUserRolesAsEntity,
  IZoneUserRole,
} from "constants/userContstants";
import { useNavigate } from "hooks/useNavigate";
import { IUser } from "interfaces/user.interface";

function AccessChip({
  getValue,
}: CellContext<UserTableRow, UserTableRow["zoneAccess"]>) {
  const { t } = useTranslation();

  const access = getValue();

  if (access === "full") {
    return <Chip color={"primary"} label={t("full")} />;
  }

  if (access === "partial") {
    return <Chip color={"secondary"} label={t("partial")} />;
  }

  return null;
}

function RolesChips({
  getValue,
}: CellContext<UserTableRow, UserTableRow["roles"]>) {
  const { t } = useTranslation();

  const roles = getValue() ?? [];

  const roleDefs = getZoneUserRolesAsEntity(t);

  return (
    <Stack
      direction={"row"}
      sx={{
        gap: 1,
      }}
    >
      {roles.map((role) => {
        const { name, activeChipTheme } = roleDefs[role];

        return (
          <Chip
            key={role}
            label={name}
            sx={(theme) => ({
              color: theme.vars.palette.texts[activeChipTheme].default,
              backgroundColor:
                theme.vars.palette.backgrounds[activeChipTheme].moderate
                  .default,
            })}
          />
        );
      })}
    </Stack>
  );
}

type UserTableRow = {
  id: string;
  userName: string | null;
  title?: string | null;
  email?: string | null;
  zoneAccess: "full" | "partial";
  roles?: IZoneUserRole[] | null;
};

type IProps = {
  users: IUser[];
  zoneId: string;
};

export function UserList({ users, zoneId }: IProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        accessorKey: "userName",
        header: t("employee"),
      },
      {
        accessorKey: "title",
        header: t("job_title"),
      },
      {
        accessorKey: "email",
        header: t("Email"),
      },
      {
        accessorKey: "zoneAccess",
        header: t("zone_access"),
        cell: AccessChip,
      },
      {
        accessorKey: "roles",
        header: t("Role(s)"),
        cell: RolesChips,
      },
    ],
    [t],
  );

  const data = useMemo(
    (): UserTableRow[] =>
      users.map((u) => ({
        id: u.id,
        userName: u.first_name + " " + u.last_name,
        title: u.profession,
        email: u.email,
        zoneAccess: u.zones?.data?.some((z) => z.id === zoneId && z.hasAccess)
          ? "full"
          : "partial",
        roles: u.roles,
      })),
    [users, zoneId],
  );

  return (
    <DataTable
      tableId={"subject-list"}
      columns={columns}
      data={data}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(row.id)}
    />
  );
}
